/* eslint-disable */
import mitt, { Emitter, Handler } from 'mitt'
import type { VideoProcessorEvents } from './types'

/**
 * Typed event bus built on top of `mitt` (~200 bytes gzipped).
 *
 * Wraps `mitt` to enforce the {@link VideoProcessorEvents} type map,
 * ensuring that event names and payload types are checked at compile time.
 */
export class EventBus {
  private emitter: Emitter<VideoProcessorEvents>

  constructor () {
    this.emitter = mitt<VideoProcessorEvents>()
  }

  /**
   * Register a handler for the given event.
   *
   * @param event   - A key of {@link VideoProcessorEvents}.
   * @param handler - Callback receiving the event payload.
   */
  on<K extends keyof VideoProcessorEvents> (
    event: K,
    handler: Handler<VideoProcessorEvents[K]>
  ): void {
    this.emitter.on(event, handler)
  }

  /**
   * Remove a previously registered handler.
   *
   * @param event   - A key of {@link VideoProcessorEvents}.
   * @param handler - The exact function reference passed to {@link on}.
   */
  off<K extends keyof VideoProcessorEvents> (
    event: K,
    handler: Handler<VideoProcessorEvents[K]>
  ): void {
    this.emitter.off(event, handler)
  }

  /**
   * Emit an event, invoking all registered handlers synchronously.
   *
   * @param event - A key of {@link VideoProcessorEvents}.
   * @param data  - Payload matching the event's type definition.
   */
  emit<K extends keyof VideoProcessorEvents> (
    event: K,
    data?: VideoProcessorEvents[K]
  ): void {
    this.emitter.emit(event, data!)
  }

  /** Remove every registered handler from all events. */
  removeAllListeners (): void {
    this.emitter.all.clear()
  }
}
